import React, { useState } from 'react';
import { FiDownload, FiBarChart2, FiFilter, FiChevronRight, FiFileText, FiInfo, FiArrowUpRight } from 'react-icons/fi';
import IconWrapper from '../components/IconWrapper';
import NCDScoreCard from '../components/ncd/NCDScoreCard';
import { NCDPrinciplesGrid, ncdPrinciples } from '../components/ncd/NCDPrinciples';
import { Heading } from '../components/ui/heading';

const ConsumerUnderstanding: React.FC = () => {
  const [activePrinciple, setActivePrinciple] = useState('consumer-understanding');
  const [view, setView] = useState<'overview' | 'compliance'>('overview');

  // Mock data
  type NcdMetric = { value: number; total: number; trend?: number; isPositive?: boolean };
  type NcdMetricsType = {
    [key: string]: NcdMetric;
  };
  
  const mockMetrics: NcdMetricsType = {
    'consumer-understanding': { value: 87, total: 100, trend: 4.2, isPositive: true },
    'products-services': { value: 75, total: 100, trend: 2.8, isPositive: true },
    'price-value': { value: 63, total: 100, trend: 1.5, isPositive: false },
    'consumer-support': { value: 92, total: 100, trend: 6.3, isPositive: true },
  };

  const activePrincipleData = ncdPrinciples.find(p => p.id === activePrinciple);

  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <Heading level={1} weight="bold" className="text-3xl mb-2">NCD Compliance</Heading>
          <p className="text-secondary/70">Monitor and evaluate compliance with NCD framework principles</p>
        </div>
        <div className="flex gap-3">
          <button className="px-4 py-2 rounded-lg text-sm flex items-center bg-white border border-gray-200 text-secondary hover:bg-gray-50 transition-colors">
            <IconWrapper icon={FiBarChart2} className="mr-2" size={18} />
            Reports
          </button>
          <button className="px-4 py-2 rounded-lg text-sm flex items-center bg-white border border-gray-200 text-secondary hover:bg-gray-50 transition-colors">
            <IconWrapper icon={FiDownload} className="mr-2" size={18} />
            Export Data
          </button>
        </div>
      </div>

      {/* View Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <div className="flex gap-8">
          <button 
            className={`pb-3 font-medium text-sm relative ${view === 'overview' ? 'text-primary' : 'text-secondary/70 hover:text-secondary'}`}
            onClick={() => setView('overview')}
          >
            Overview
            {view === 'overview' && <span className="absolute bottom-0 left-0 w-full h-0.5 bg-primary"></span>}
          </button>
          <button 
            className={`pb-3 font-medium text-sm relative ${view === 'compliance' ? 'text-primary' : 'text-secondary/70 hover:text-secondary'}`}
            onClick={() => setView('compliance')}
          >
            Compliance Requirements
            {view === 'compliance' && <span className="absolute bottom-0 left-0 w-full h-0.5 bg-primary"></span>}
          </button>
        </div>
      </div>

      {view === 'overview' ? (
        <>
          {/* Overall Score */}
          <div className="mb-8">
            <NCDScoreCard 
              title="Overall NCD Compliance Score"
              score={79}
              maxScore={100}
              previousScore={75}
              description="Composite score across all NCD principles and requirements"
              details="Meets regulatory expectations with room for improvement in Price and Value transparency"
            />
          </div>

          {/* NCD Principles */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-secondary">NCD Principles</h2>
              <button className="text-primary text-sm font-medium flex items-center hover:underline">
                Learn more about NCD Framework
                <IconWrapper icon={FiChevronRight} className="ml-1" size={16} />
              </button>
            </div>
            <NCDPrinciplesGrid 
              metrics={mockMetrics}
              activePrinciple={activePrinciple}
              onSelectPrinciple={setActivePrinciple}
            />
          </div>

          {/* Selected Principle Details */}
          {activePrincipleData && (
            <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
              <div className="flex justify-between items-start mb-6">
                <div className="flex items-center">
                  <div 
                    className="p-3 rounded-lg mr-4" 
                    style={{ backgroundColor: `${activePrincipleData.color}20`, color: activePrincipleData.color }}
                  >
                    <IconWrapper icon={activePrincipleData.icon} size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-secondary">{activePrincipleData.name}</h3>
                    <p className="text-sm text-secondary/70">{activePrincipleData.description}</p>
                  </div>
                </div>
                <button className="text-primary text-sm font-medium flex items-center hover:underline">
                  View Full Report
                  <IconWrapper icon={FiArrowUpRight} className="ml-1" size={16} />
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-secondary/70 mb-2">Key Criteria</h4>
                    <div className="space-y-2">
                      {activePrincipleData.criteria.map((criterion, index) => (
                        <div key={index} className="flex items-center bg-gray-50 p-3 rounded-lg">
                          <IconWrapper icon={FiInfo} className="mr-3 text-primary flex-shrink-0" size={16} />
                          <p className="text-sm">{criterion}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-secondary/70 mb-2">Compliance Summary</h4>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="mb-3">
                        <div className="text-sm text-secondary/70">Score</div>
                        <div className="text-2xl font-bold text-secondary">
                          {mockMetrics[activePrinciple].value}
                          <span className="text-sm text-secondary/50 ml-1">/ {mockMetrics[activePrinciple].total}</span>
                        </div>
                      </div>
                      <div className="mb-3">
                        <div className="text-sm text-secondary/70">Trend</div>
                        <div className={`text-sm font-medium ${mockMetrics[activePrinciple].isPositive ? 'text-green-500' : 'text-red-500'}`}>
                          {mockMetrics[activePrinciple].isPositive ? '↑' : '↓'} {mockMetrics[activePrinciple].trend}% vs last quarter
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-secondary/70">Actions Required</div>
                        <div className="text-sm font-medium">
                          {mockMetrics[activePrinciple].value < 70 ? 'High Priority' : mockMetrics[activePrinciple].value < 90 ? 'Medium Priority' : 'Low Priority'}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-secondary/70 mb-2">Related Complaints</h4>
                    <div className="space-y-2">
                      {[1, 2, 3].map((item) => (
                        <div key={item} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center">
                            <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center mr-3">
                              <IconWrapper icon={FiFileText} size={16} />
                            </div>
                            <span className="text-sm font-medium">T-00{item}</span>
                          </div>
                          <button className="text-primary text-xs font-medium hover:underline">View</button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        // Compliance Requirements View
        <div className="flex flex-col items-center justify-center py-12">
          <IconWrapper icon={FiBarChart2} className="text-secondary/30 mb-4" size={48} />
          <h2 className="text-xl font-medium mb-2">Compliance Requirements View</h2>
          <p className="text-secondary/70 max-w-lg text-center mb-6">
            This section will provide detailed compliance requirements and tracking for each NCD principle.
          </p>
          <button className="px-4 py-2 rounded-full text-sm bg-primary text-secondary hover:bg-primary/90 transition-colors">
            View Requirements
          </button>
        </div>
      )}
    </div>
  );
};

export default ConsumerUnderstanding;